import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { Reveal, FadeUp } from "@/components/motion/Reveal";
import { ASSETS, SITE } from "@/lib/assets";

const VALUES = [
  { n: "01", t: "The Word, Uncompromised", d: "Scripture is our foundation. We preach it whole — comforting and confronting — without apology." },
  { n: "02", t: "Prayer & Desperation", d: "Nothing of eternal weight happens without prayer. We contend on our knees before we move our feet." },
  { n: "03", t: "Bold Evangelism", d: "The Gospel is good news meant to be spoken. We refuse to keep silent while a nation sleeps." },
  { n: "04", t: "Multiplying Disciples", d: "We measure success not in crowds but in Christ-followers who make other Christ-followers." },
];

const STATS = [
  { k: "3", v: "Core Commands" },
  { k: "ON", v: "Rooted in Ontario" },
  { k: "∞", v: "One Gospel" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        testId="about-header"
        label="Who We Are"
        title={<>A Ministry For A<br /><span className="text-wutn">Sleeping</span> Nation</>}
        subtitle="Wake-Up The Nation is a Canadian evangelistic ministry led by Kerby Stivene — awakening the Church, discipling believers, and carrying the Gospel across the country."
        image={ASSETS.preachingEvent1}
      />

      {/* Story + portrait */}
      <section className="bg-black py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="relative overflow-hidden">
                <img src={ASSETS.preachingEvent2} alt="Kerby Stivene preaching" className="w-full object-cover clip-slant" />
                <div className="absolute inset-0 spotlight" />
              </div>
            </Reveal>
            <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mt-px">
              {STATS.map((s) => (
                <div key={s.v} className="bg-black p-6 text-center">
                  <div className="font-display text-4xl text-wutn">{s.k}</div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-zinc-500">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 lg:pt-6">
            <SectionLabel>The Story</SectionLabel>
            <h2 className="mt-5 font-display uppercase text-4xl md:text-6xl leading-[0.9]">Born From A <span className="text-wutn">Burden</span></h2>
            <div className="mt-8 space-y-5 text-zinc-400 text-lg leading-relaxed">
              <p>Across Canada, church buildings stand while hearts grow cold. Wake-Up The Nation was born from a holy discontent — a refusal to accept a slumbering Church as the final word.</p>
              <p>Founded and led by <span className="text-white font-semibold">Pastor & Evangelist Kerby Stivene</span>, the ministry proclaims that there is <span className="text-white font-semibold">no other name</span> under heaven by which we must be saved. From Sundridge, Ontario, the message travels to campuses, cities, and small towns alike.</p>
              <p>We are not interested in religious entertainment. We are after revival — believers set ablaze, formed into disciples, and sent to share the Gospel with courage.</p>
            </div>
            <a href={SITE.youtube} target="_blank" rel="noreferrer" data-testid="about-youtube-btn" className="mt-10 inline-flex items-center gap-2 bg-wutn hover:bg-wutn-dark text-white px-7 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
              Hear The Message <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="relative bg-[#0A0A0A] border-y border-white/10 py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Quote className="mx-auto h-10 w-10 text-wutn" />
          <blockquote className="mt-8 font-display uppercase text-3xl md:text-5xl leading-[1.05]">
            “You don&apos;t need much to <span className="text-wutn">tell about Jesus</span> — you just need to open your mouth.”
          </blockquote>
          <p className="mt-8 font-cond uppercase tracking-[0.2em] text-zinc-400 text-sm">Kerby Stivene · {SITE.short}</p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-black py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionLabel>What Drives Us</SectionLabel>
          <h2 className="mt-5 font-display uppercase text-5xl md:text-7xl leading-[0.9] mb-14">Our <span className="text-wutn">Convictions</span></h2>
          <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {VALUES.map((v, i) => (
              <FadeUp key={v.n} delay={(i % 2) * 0.08}>
                <div className="bg-black p-8 md:p-12 h-full">
                  <span className="font-display text-5xl text-outline-red">{v.n}</span>
                  <h3 className="mt-5 font-display uppercase text-2xl md:text-3xl">{v.t}</h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{v.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
